import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from './components/ErrorBoundary';
import ProductCatalog from './components/ProductCatalog';
import ProductDetail from './components/ProductDetail';
import ScrollToTop from './components/ScrollToTop';

// Helper to retry lazy chunk imports and reload on stale deploy
function lazyWithRetry(factory) {
  return lazy(async () => {
    const key = 'chunk-reloaded';
    try {
      const module = await factory();
      sessionStorage.removeItem(key);
      return module;
    } catch (err) {
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return new Promise(() => {}); // halt render until reload
      }
      throw err;
    }
  });
}

const CartSummary = lazyWithRetry(() => import('./components/CartSummary'));
const OrderTracker = lazyWithRetry(() => import('./components/OrderTracker'));
const AIResearchLab = lazyWithRetry(() => import('./components/AIResearchLab'));
const VendorDashboard = lazyWithRetry(() => import('./components/VendorDashboard'));
const AdminDashboard = lazyWithRetry(() => import('./components/AdminDashboard'));
const BuyerDashboard = lazyWithRetry(() => import('./components/BuyerDashboard'));
const FinancierDashboard = lazyWithRetry(() => import('./components/FinancierDashboard'));
const SpecialistsPortal = lazyWithRetry(() => import('./components/SpecialistsPortal'));
const MachineryHireDashboard = lazyWithRetry(() => import('./components/MachineryHireDashboard'));
const MachineryCatalog = lazyWithRetry(() => import('./components/MachineryCatalog'));
const LogisticsPortal = lazyWithRetry(() => import('./components/LogisticsPortal'));
const AboutPage = lazyWithRetry(() => import('./components/AboutPage'));

import BoqModal from './components/BoqModal';
import AuthModal from './components/AuthModal';
import TawkToChat from './components/TawkToChat';
import LegalCharterModal from './components/LegalCharterModal';
import {
  isAuthenticated,
  logoutUser,
  getUserProfile,
} from './components/authService';

// Public site URL constant for canonical links and metadata
export const PUBLIC_SITE_URL = 'https://ebakasi.com';

// Auth-gated route wrapper component
function ProtectedRoute({ children }) {
  return isAuthenticated() ? children : <Navigate to="/" replace />;
}

// Vendor route wrapper - only vendors (and admins)
function VendorProtectedRoute({ children }) {
  const user = getUserProfile();
  const isAuthorized = user?.role === 'vendor' || user?.role === 'admin' || !!user?.adminTier;
  return isAuthorized ? children : <Navigate to="/" replace />;
}

// Machinery hire route wrapper - only machinery_hire role (and admins)
function MachineryHireProtectedRoute({ children }) {
  const user = getUserProfile();
  const isAuthorized = user?.role === 'machinery_hire' || user?.role === 'admin' || !!user?.adminTier;
  return isAuthorized ? children : <Navigate to="/" replace />;
}

// Financier route wrapper - only financier role, SUPER_ADMIN, or FINANCE_ADMIN
function FinancierProtectedRoute({ children }) {
  const user = getUserProfile();
  const isAuthorized = user?.role === 'financier' || user?.adminTier === 'SUPER_ADMIN' || user?.adminTier === 'FINANCE_ADMIN';
  return isAuthorized ? children : <Navigate to="/" replace />;
}

// Admin route wrapper - only admin roles (any adminTier)
function AdminProtectedRoute({ children }) {
  const user = getUserProfile();
  const isAuthorized = user?.role === 'admin' || !!user?.adminTier;
  return isAuthorized ? children : <Navigate to="/" replace />;
}

// Logistics route wrapper - only SUPER_ADMIN, FINANCE_ADMIN, and LOGISTICS_ADMIN
function LogisticsProtectedRoute({ children }) {
  const user = getUserProfile();
  const isAuthorized = user?.role === 'SUPER_ADMIN' ||
    ['SUPER_ADMIN', 'FINANCE_ADMIN', 'LOGISTICS_ADMIN'].includes(user?.adminTier);
  return isAuthorized ? children : <Navigate to="/" replace />;
}

function AppContent() {
  // Global sovereign in-app alert toast notification tracking layer
  const [globalToast, setGlobalToast] = useState(null);
  
  React.useEffect(() => {
    window.alert = (message) => {
      if (!message || (typeof message !== "string" && typeof message !== "number")) return;
      const msgStr = String(message);
      setGlobalToast(msgStr);
      
      // Auto-dismiss completely after exactly 4 seconds
      setTimeout(() => {
        setGlobalToast(current => current === msgStr ? null : current);
      }, 8000);
    };
  }, []);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const checkAuthStatus = () => {
    if (typeof isAuthenticated === 'function') return isAuthenticated();
    return !!localStorage.getItem('ebakasiJwtToken');
  };
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const totalCartPieces = cart.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const [category, setCategory] = useState('');
  const [sortPrice, setSortPrice] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');

  // FIXED PERIMETER SWITCH CORE: DEFAULTS FALSE TO KEEP POPUPS HIDDEN ON BOOT
  const [isAuthOpen, setIsAuthOpen] = useState(false);

  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [isMfaVerifying, setIsMfaVerifying] = useState(false);
  const [isBoqOpen, setIsBoqOpen] = useState(false);
  const [showLegalCharter, setShowLegalCharter] = useState(false);
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);
  
  // PWA Install Prompt State
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [installSnoozed, setInstallSnoozed] = useState(false);
  const [showIosTooltip, setShowIosTooltip] = useState(false);
  const [isAppInstalled, setIsAppInstalled] = useState(false);

  // PWA Update Available State
  const [updateReady, setUpdateReady] = useState(false);
  const [waitingRegistration, setWaitingRegistration] = useState(null);
  
  // 🌟 2-STATE THEME ARCHITECTURE: dark ↔ grey
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('ebakasiTheme3');
    // Migrate old values to grey
    if (stored === 'light' || stored === 'high-contrast') {
      localStorage.setItem('ebakasiTheme3', 'grey');
      return 'grey';
    }
    return stored || 'dark';
  });

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'grey' : 'dark';
    setTheme(nextTheme);
    localStorage.setItem('ebakasiTheme3', nextTheme);
  };

  // SYSTEM SUPPORT LOGISTICS SETTINGS LEDGER
  const [settings, setSettings] = useState({
    whatsappSupport: '+256790206354',
    emailSupport: 'lawrenceomoit66@gmail.com',
    wholesaleDiscountThreshold: 20000000,
    wholesaleDiscountPercent: 0.5,
  });

  const baseUrl = 'https://ebakasi.onrender.com';

  const navigateToView = (targetView) => {
    const pathMap = {
      'catalog': '/',
      'engineers': '/specialists',
      'cart': '/cart',
      'orders': '/orders',
      'lab': '/lab',
      'dashboard': '/dashboard',
      'vendor': '/vendor',
      'machinery-hire': '/machinery-hire',
      'admin': '/admin',
      'machinery': '/machinery',
      'logistics': '/logistics',
      'about': '/about',
    };
    navigate(pathMap[targetView] || '/');
  };

  const updateGlobalSettingsStateBroadcast = (newUpdatedConstants) => {
    setSettings((prev) => ({ ...prev, ...newUpdatedConstants }));
  };
  useEffect(() => {
    const synchronizeEcosystemCatalog = async () => {
      const url = `${baseUrl}/api/products`;
      try {
        const res = await fetch(url);
        const data = await res.json();
        if (res.ok) {
          if (Array.isArray(data)) {
            setProducts(data);
          } else if (data && Array.isArray(data.products)) {
            setProducts(data.products);
          }
        }
      } catch (err) {
        console.error(err);
      }
    };
    synchronizeEcosystemCatalog();
  }, []);

  useEffect(() => {
    const fetchSystemConstants = async () => {
      const token = localStorage.getItem('ebakasiJwtToken');
      if (!token) return;
      try {
        const res = await fetch(`${baseUrl}/api/admin/global-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data) {
          setSettings(data);
        }
      } catch (err) {
        console.log('Running parameters on defaults.');
      }
    };
    fetchSystemConstants();
  }, []);

  // PWA Install Prompt Handler
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  // PWA App Installed Handler - detects installation via browser menu
  useEffect(() => {
    const handleAppInstalled = () => {
      setShowInstallButton(false);
      setDeferredPrompt(null);
      setIsAppInstalled(true);
      // Persist installed flag so chip stays hidden across reloads
      localStorage.setItem('ebakasiAppInstalled', 'true');
    };

    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, []);

  // Check if app is already installed on mount
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    const wasInstalled = localStorage.getItem('ebakasiAppInstalled') === 'true';
    
    if (isStandalone || wasInstalled) {
      setIsAppInstalled(true);
      setShowInstallButton(false);
    }
  }, []);

  const [headerHeight, setHeaderHeight] = useState(72);
    
    useEffect(() => {
      const recounterCeiling = () => {
        const headerEl = window.document.querySelector("header") || window.document.querySelector("nav");
        if (headerEl) {
          setHeaderHeight(headerEl.offsetHeight);
        }
      };
      // Fire immediately on initialization mount loops
      recounterCeiling();
      
      // Bind live structural event listeners tracking layout shifts or device rotations reactively
      window.addEventListener("resize", recounterCeiling);
      return () => window.removeEventListener("resize", recounterCeiling);
    }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstallButton(false);
    }
    setDeferredPrompt(null);
  };

  // PWA Update Available Handler
  useEffect(() => {
    const handleUpdateAvailable = (event) => {
      setWaitingRegistration(event.detail);
      setUpdateReady(true);
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable);
    return () => window.removeEventListener('sw-update-available', handleUpdateAvailable);
  }, []);

  const handleAppUpdate = () => {
    if (waitingRegistration && waitingRegistration.waiting) {
      waitingRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  // iOS Safari detection for install hint
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  // Detect if app is running in standalone/installed mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  // Check if install chip should be visible
  const shouldShowInstallChip = !isStandalone && !isAppInstalled && !installSnoozed && (showInstallButton || isIOS);

  // Handle install chip dismiss/snooze
  const handleSnoozeInstall = () => {
    setInstallSnoozed(true);
    // Snooze for current session only (sessionStorage would be cleared on close)
    // Using localStorage with timestamp for 24h snooze
    localStorage.setItem('ebakasiInstallSnoozed', Date.now().toString());
  };

  // Check snooze status on mount
  useEffect(() => {
    const snoozedAt = localStorage.getItem('ebakasiInstallSnoozed');
    if (snoozedAt) {
      const hoursSinceSnooze = (Date.now() - parseInt(snoozedAt)) / (1000 * 60 * 60);
      if (hoursSinceSnooze < 24) {
        setInstallSnoozed(true);
      } else {
        // Snooze expired, clear it
        localStorage.removeItem('ebakasiInstallSnoozed');
      }
    }
  }, []);

  // Handle iOS install chip click
  const handleIosInstallClick = () => {
    setShowIosTooltip(!showIosTooltip);
  };

  const handleMfaVerificationSubmit = (e) => {
    e.preventDefault();
    if (mfaCode.length !== 6) {
      alert(
        'Administrative Security Guard: Verification tokens must be exactly 6 chronological numeric digits.'
      );
      return;
    }
    setIsMfaVerifying(true);
    setTimeout(() => {
      setIsMfaVerifying(false);
      setShowMfaModal(false);
      alert(`🎉 Identity Verified! 2FA Security Handshake Complete.`);
    }, 1500);
  };
  return (
    <div
      style={{
        backgroundColor: 'var(--bg-main)',
        minHeight: '100vh',
        color: 'var(--text-primary)',
        fontFamily: 'system-ui, sans-serif',
        maxWidth: '100%',
        overflowX: 'clip',
      }}
    >
      {globalToast && (
        <div style={{
          position: "fixed",
          bottom: "24px",
          right: "24px",
          backgroundColor: "var(--bg-card, #1e293b)",
          borderLeft: "4px solid var(--accent-shout, #22c55e)",
          color: "var(--text-primary, #fff)",
          padding: "12px 20px",
          borderRadius: "6px",
          fontSize: "13px",
          fontWeight: "700",
          boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
          zIndex: 999999,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          minWidth: "280px",
          maxWidth: "420px",
          boxSizing: "border-box"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>⚡ {globalToast}</span>
          </div>
          <button 
            onClick={() => setGlobalToast(null)} 
            type="button"
            style={{ 
              background: "none", 
              border: "none", 
              color: "var(--text-secondary, #94a3b8)", 
              cursor: "pointer", 
              fontWeight: "900", 
              fontSize: "16px",
              padding: "0 4px"
            }}
          >
            &times;
          </button>
        </div>
      )}

      {/* 🎨 DYNAMIC VARIABLE SHEET CONTROLLER MATRIX */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
                :root {
                    --bg-main: ${theme === 'dark' ? '#0f172a' : '#d1d5db'};
                    --bg-card: ${theme === 'dark' ? '#1e293b' : '#e5e7eb'};
                    --border-line: ${theme === 'dark' ? '#334155' : '#6b7280'};
                    --text-primary: ${theme === 'dark' ? '#ffffff' : '#0f172a'};
                    --text-secondary: ${theme === 'dark' ? '#94a3b8' : '#334155'};
                    --accent-shout: #ea580c;
                }
                .premium-nav-btn {
                    background-color: transparent;
                    border: 1px solid var(--border-line);
                    border-radius: 20px;
                    padding: 5px 12px;
                    font-weight: 700;
                    font-size: 13px;
                    color: var(--text-primary);
                    cursor: pointer;
                    transition: all 0.2s ease;
                    white-space: nowrap;
                }
                .premium-nav-btn:hover {
                    background-color: rgba(148, 163, 184, 0.12);
                    border-color: var(--accent-shout);
                    color: var(--accent-shout) !important;
                    transform: translateY(-1px);
                }
                .action-btn-pill {
                    font-size: 13.5px !important; font-weight: 800 !important; letter-spacing: 0.5px; border: 2px solid var(--accent-shout); border-radius: 30px; cursor: pointer;
                }
                .theme-toggle-container {
                    display: inline-flex; align-items: center; justify-content: space-between;
                    background-color: #0f172a; border: 2px solid #ea580c; padding: 4px 8px;
                    border-radius: 30px; cursor: pointer; position: relative; width: 85px; height: 32px;
                    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1); box-sizing: border-box;
                }
            `,
        }}
      />

      {/* 🏢 DYNAMIC REACTIVE CORE INTERFACE SYSTEM HOUSING */}
      <div
        style={{
          backgroundColor: 'var(--bg-main)',
          minHeight: '100vh',
          color: 'var(--text-primary)',
          transition: 'all 0.2s',
          maxWidth: '100%',
          overflowX: 'clip',
        }}
      >
        {/* FLOATING STICKY NAVIGATION HEADER MATRIX */}
        <header
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: 'var(--bg-card)',
            borderBottom: '2px solid var(--border-line)',
            padding: '12px 20px',
            zIndex: 2000,
            boxShadow: '0 4px 25px rgba(15, 23, 42, 0.5)',
          }}
        >
          <div
            style={{
              maxWidth: '1400px',
              margin: '0 auto',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {/* MOBILE HAMBURGER */}
            <button
              className="mobile-hamburger-btn"
              onClick={() => setIsNavDrawerOpen(true)}
              style={{
                display: 'none',
                padding: '8px 12px',
                backgroundColor: 'transparent',
                color: 'var(--text-primary)',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
              }}
            >
              ☰
            </button>

            {/* 🏛️ ELITE LOGO CONTAINER GROUP */}
            <div
              onClick={() => navigateToView('catalog')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
              }}
            >
              <img
                src="/ebakasi-logo.png"
                alt="Ebakasi Industrial Uganda Logo"
                style={{
                  width: '36px',
                  height: '36px',
                  objectFit: 'contain',
                  borderRadius: '6px',
                }}
              />
              <span
                style={{
                  fontWeight: '900',
                  fontSize: '20px',
                  color: '#fff',
                  letterSpacing: '0.5px',
                }}
              >
                EBAKASI
              </span>
            </div>

            {/* RIGHT ZONE: Amazon-style grouped layout */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '24px',
              }}
            >
              {/* Mobile-responsive wrapper: hide secondary controls on narrow screens */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
                className="header-utility-cluster"
              >
                {/* 💡 ANIMATING 3-TIER TOGGLE SWITCH */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: '800',
                      color: 'var(--text-secondary)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {theme === 'dark'
                      ? '🔒 Dark'
                      : theme === 'light'
                        ? '☀️ Light'
                        : '⚡ High-Vis'}
                  </span>
                  <div
                    className="theme-toggle-container"
                    onClick={toggleTheme}
                  >
                    <span
                      style={{ fontSize: '11px', zIndex: 1, paddingLeft: '2px' }}
                    >
                      🔒
                    </span>
                    <span
                      style={{ fontSize: '11px', zIndex: 1, paddingRight: '2px' }}
                    >
                      ☀️
                    </span>
                  </div>
                </div>
              </div>

              {/* (b) Account/Action Cluster: Sign In/Exit + Upload BOQ + Cart */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
                className="header-account-cluster"
              >
                {/* Sign In / Exit Button - visible on both desktop and mobile */}
                {!isAuthenticated() ? (
                  <button
                    className="action-btn-pill"
                    onClick={() => setIsAuthOpen(true)}
                    style={{
                      padding: '5px 10px', fontSize: '12px', whiteSpace: 'nowrap', lineHeight: 1,
                      backgroundColor: 'transparent',
                      color: 'var(--text-primary)',
                    }}
                  >
                    Sign In
                  </button>
                ) : (
                  <button
                    className="action-btn-pill"
                    onClick={() => {
                      logoutUser();
                      window.location.reload();
                    }}
                    style={{
                      padding: '5px 10px', fontSize: '12px', whiteSpace: 'nowrap', lineHeight: 1,
                      backgroundColor: 'var(--border-line)',
                      color: 'var(--text-primary)',
                      border: 'none',
                    }}
                  >
                    🚪 Exit
                  </button>
                )}

                {/* Upload BOQ button - desktop only */}
                <button
                  className="premium-nav-btn upload-boq-header-btn"
                  onClick={() => setIsBoqOpen(true)}
                  style={{
                    padding: '5px 10px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                  }}
                >
                  📋 Upload BOQ
                </button>

                {/* Tawk.to Live Support Widget */}
                <TawkToChat />

                {/* Cart button with count badge */}
                <button
                  className="premium-nav-btn"
                  onClick={() => navigateToView('cart')}
                >
                  🛒 Cart
                  <span
                    className="cart-badge-text"
                    style={{
                      marginLeft: '5px',
                      backgroundColor: '#ea580c',
                      color: 'var(--text-primary)',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      fontSize: '11px',
                    }}
                  >
                    {cart.length} Item{cart.length !== 1 ? 's' : ''} <span className="cart-pcs-suffix">/ {totalCartPieces} Pcs</span>
                  </span>
                </button>
              </div>

              {/* (c) Desktop Nav Buttons Zone - keeps existing structure */}
              <div className="desktop-nav-buttons">
                <style>{`
                  @media (max-width: 1023px) {
                    .desktop-nav-buttons {
                      display: none !important;
                    }
                  }
                  @media (max-width: 1023px) {
                    .mobile-hamburger-btn {
                      display: inline-flex !important;
                    }
                  }
                  @media (min-width: 1024px) {
                    .mobile-hamburger-btn {
                      display: none !important;
                    }
                  }
                  @media (max-width: 1023px) {
                    .header-utility-cluster {
                      display: none !important;
                    }
                  }
                  @media (max-width: 768px) {
                    .header-account-cluster {
                      gap: 8px !important;
                    }
                    .cart-badge-text {
                      font-size: 10px !important;
                      padding: 2px 4px !important;
                    }
                    .cart-pcs-suffix {
                      display: none !important;
                    }
                    .upload-boq-header-btn {
                      display: none !important;
                    }
                  }
                `}</style>
                <button
                  className="premium-nav-btn"
                  onClick={() => navigateToView('catalog')}
                >
                  Catalog
                </button>
                <button
                  className="premium-nav-btn"
                  onClick={() => navigateToView('orders')}
                >
                  Track Orders
                </button>
                {checkAuthStatus() && (
                  <button
                    className="premium-nav-btn"
                    onClick={() => navigateToView('dashboard')}
                  >
                    My Dashboard
                  </button>
                )}
                <button
                  className="premium-nav-btn"
                  onClick={() => navigateToView('lab')}
                >
                  🤖 Ebakasi AI Assistant
                </button>
                <button
                  className="premium-nav-btn"
                  onClick={() => navigateToView('machinery')}
                >
                  🏗️ Heavy Infrastructure Asset Lease Vault
                </button>
                {(typeof isAuthenticated === 'function'
                  ? isAuthenticated()
                  : !!localStorage.getItem('ebakasiJwtToken')) &&
                  (getUserProfile()?.role === 'SUPER_ADMIN' ||
                    ['SUPER_ADMIN', 'FINANCE_ADMIN', 'LOGISTICS_ADMIN'].includes(getUserProfile()?.adminTier)) && (
                    <button
                      className="premium-nav-btn"
                      onClick={() => navigateToView('logistics')}
                    >
                      🚚 Driver Network
                    </button>
                  )}

                {(typeof isAuthenticated === 'function'
                  ? isAuthenticated()
                  : !!localStorage.getItem('ebakasiJwtToken')) &&
                  getUserProfile()?.role === 'vendor' && (
                    <button
                      className="premium-nav-btn"
                      onClick={() => navigateToView('vendor')}
                      style={{ color: 'var(--accent-shout)' }}
                    >
                      🏭 Vendor Desk
                    </button>
                  )}

                {(typeof isAuthenticated === 'function'
                  ? isAuthenticated()
                  : !!localStorage.getItem('ebakasiJwtToken')) &&
                  getUserProfile()?.role === 'machinery_hire' && (
                    <button
                      className="premium-nav-btn"
                      onClick={() => navigateToView('machinery-hire')}
                      style={{ color: 'var(--accent-shout)' }}
                    >
                      🚜 Machinery Desk
                    </button>
                  )}

                {((isAuthenticated && isAuthenticated()) || !!localStorage.getItem('ebakasiJwtToken')) && (getUserProfile()?.role === 'admin' || getUserProfile()?.role === 'SUPER_ADMIN' || !!getUserProfile()?.adminTier || localStorage.getItem('isAdmin') === 'true') && (
                  <button
                    className="premium-nav-btn"
                    onClick={() => navigateToView('admin')}
                    style={{ color: '#ef4444' }}
                  >
                    {(() => {
                      const tier = getUserProfile()?.adminTier;
                      switch (tier) {
                        case 'SUPER_ADMIN': return '🛠️ SUPER_ADMIN';
                        case 'FINANCE_ADMIN': return '💰 Finance Admin';
                        case 'INVESTOR_AUDITOR': return '📊 Investor Admin';
                        case 'OPERATIONS_ADMIN': return '⚙️ Operations Admin';
                        case 'SUPPORT_ADMIN': return '🎧 Support Admin';
                        case 'LOGISTICS_ADMIN': return '🚚 Logistics Admin';
                        case 'SECURITY_AUDITOR': return '🔒 Security Auditor';
                        default: return '🛠️ Admin';
                      }
                    })()}
                  </button>
                )}

                <button
                  className="action-btn-pill"
                  onClick={() => setIsBoqOpen(true)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#ea580c',
                    color: '#fff',
                    border: 'none',
                  }}
                >
                  📋 Upload BOQ
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* DRAWER SCRIM */}
        {isNavDrawerOpen && (
          <div
            onClick={() => setIsNavDrawerOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 9998,
            }}
          />
        )}

        {/* MOBILE NAV DRAWER */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: isNavDrawerOpen ? 0 : '-320px',
            width: '80%',
            maxWidth: '320px',
            height: '100vh',
            backgroundColor: 'var(--bg-card)',
            zIndex: 9999,
            overflowY: 'auto',
            transition: 'transform 0.3s ease',
            padding: '16px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div
              onClick={() => { navigate('/'); setIsNavDrawerOpen(false); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
              }}
            >
              <img
                src="/ebakasi-logo.png"
                alt="Ebakasi Industrial Uganda Logo"
                style={{
                  width: '36px',
                  height: '36px',
                  objectFit: 'contain',
                  borderRadius: '6px',
                }}
              />
              <span
                style={{
                  fontWeight: '900',
                  fontSize: '20px',
                  color: 'var(--text-primary)',
                  letterSpacing: '0.5px',
                }}
              >
                EBAKASI
              </span>
            </div>
            <button
              onClick={() => setIsNavDrawerOpen(false)}
              style={{
                padding: '4px 8px',
                backgroundColor: 'transparent',
                color: 'var(--text-primary)',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              className="premium-nav-btn"
              onClick={() => { navigate('/order-tracker'); setIsNavDrawerOpen(false); }}
              style={{
                padding: '12px',
                backgroundColor: 'var(--bg-main)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-line)',
                borderRadius: '6px',
                fontWeight: '700',
                fontSize: '14px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              Track Orders
            </button>
            {((isAuthenticated && isAuthenticated()) || !!localStorage.getItem('ebakasiJwtToken')) && (
              <button
                className="premium-nav-btn"
                onClick={() => { navigate('/dashboard?tab=analytics'); setIsNavDrawerOpen(false); }}
                style={{
                  padding: '12px',
                  backgroundColor: 'var(--bg-main)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-line)',
                  borderRadius: '6px',
                  fontWeight: '700',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                My Dashboard
              </button>
            )}
            <button
              className="premium-nav-btn"
              onClick={() => { navigate('/lab'); setIsNavDrawerOpen(false); }}
              style={{
                padding: '12px',
                backgroundColor: 'var(--bg-main)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-line)',
                borderRadius: '6px',
                fontWeight: '700',
                fontSize: '14px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              🤖 Ebakasi AI Assistant
            </button>
            <button
              className="premium-nav-btn"
              onClick={() => { navigate('/specialists'); setIsNavDrawerOpen(false); }}
              style={{
                padding: '12px',
                backgroundColor: 'var(--bg-main)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-line)',
                borderRadius: '6px',
                fontWeight: '700',
                fontSize: '14px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              👷 Engineers
            </button>
            <button
              className="premium-nav-btn"
              onClick={() => { navigate('/machinery'); setIsNavDrawerOpen(false); }}
              style={{
                padding: '12px',
                backgroundColor: 'var(--bg-main)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-line)',
                borderRadius: '6px',
                fontWeight: '700',
                fontSize: '14px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              🏗️ Heavy Infrastructure...
            </button>
            {((isAuthenticated && isAuthenticated()) || !!localStorage.getItem('ebakasiJwtToken')) && (getUserProfile()?.role === 'logistics_admin' || getUserProfile()?.adminTier === 'LOGISTICS_ADMIN' || localStorage.getItem('isLogisticsAdmin') === 'true') && (
              <button
                className="premium-nav-btn"
                onClick={() => { navigate('/logistics'); setIsNavDrawerOpen(false); }}
                style={{
                  padding: '12px',
                  backgroundColor: 'var(--bg-main)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-line)',
                  borderRadius: '6px',
                  fontWeight: '700',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                🚚 Driver Network
              </button>
            )}
            {((isAuthenticated && isAuthenticated()) || !!localStorage.getItem('ebakasiJwtToken')) && (getUserProfile()?.role === 'vendor' || localStorage.getItem('isVendor') === 'true') && (
              <button
                className="premium-nav-btn"
                onClick={() => { navigate('/vendor?tab=products'); setIsNavDrawerOpen(false); }}
                style={{
                  padding: '12px',
                  backgroundColor: 'var(--bg-main)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-line)',
                  borderRadius: '6px',
                  fontWeight: '700',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                🏭 Vendor Desk
              </button>
            )}
            {((isAuthenticated && isAuthenticated()) || !!localStorage.getItem('ebakasiJwtToken')) && (getUserProfile()?.role === 'machinery_hire' || localStorage.getItem('isMachineryHire') === 'true') && (
              <button
                className="premium-nav-btn"
                onClick={() => { navigate('/machinery-hire'); setIsNavDrawerOpen(false); }}
                style={{
                  padding: '12px',
                  backgroundColor: 'var(--bg-main)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-line)',
                  borderRadius: '6px',
                  fontWeight: '700',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                🚜 Machinery Desk
              </button>
            )}
            {((isAuthenticated && isAuthenticated()) || !!localStorage.getItem('ebakasiJwtToken')) && (getUserProfile()?.role === 'admin' || getUserProfile()?.role === 'SUPER_ADMIN' || !!getUserProfile()?.adminTier || localStorage.getItem('isAdmin') === 'true') && (
              <button
                className="premium-nav-btn"
                onClick={() => { navigate('/admin?pane=overview'); setIsNavDrawerOpen(false); }}
                style={{
                  padding: '12px',
                  backgroundColor: 'var(--bg-main)',
                  color: '#ef4444',
                  border: '1px solid var(--border-line)',
                  borderRadius: '6px',
                  fontWeight: '700',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                {(() => {
                  const tier = getUserProfile()?.adminTier;
                  switch (tier) {
                    case 'SUPER_ADMIN': return '🛠️ SUPER_ADMIN';
                    case 'FINANCE_ADMIN': return '💰 Finance Admin';
                    case 'INVESTOR_AUDITOR': return '📊 Investor Admin';
                    case 'OPERATIONS_ADMIN': return '⚙️ Operations Admin';
                    case 'SUPPORT_ADMIN': return '🎧 Support Admin';
                    case 'LOGISTICS_ADMIN': return '🚚 Logistics Admin';
                    case 'SECURITY_AUDITOR': return '🔒 Security Auditor';
                    default: return '🛠️ Admin';
                  }
                })()}
              </button>
            )}
            <button
              className="action-btn-pill"
              onClick={() => { setIsBoqOpen(true); setIsNavDrawerOpen(false); }}
              style={{
                padding: '12px',
                backgroundColor: '#ea580c',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '700',
                fontSize: '14px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              📋 Upload BOQ
            </button>
          </div>

          {/* WHATSAPP SUPPORT IN DRAWER */}
          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--border-line)' }}>
            <a
              href={`https://wa.me/${String(settings.whatsappSupport).replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px',
                backgroundColor: '#25D366',
                border: '2px solid #128C7E',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '13px',
                fontWeight: '800',
                textDecoration: 'none',
                textAlign: 'center',
                justifyContent: 'center',
              }}
            >
              Live WhatsApp Desk
            </a>
          </div>

          {/* THEME TOGGLE IN DRAWER */}
          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--border-line)' }}>
            <div className="theme-toggle-container" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                {theme === 'dark' ? '🌙 Dark' : '☀️ Grey'}
              </span>
              <button
                onClick={() => { toggleTheme(); }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--bg-main)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-line)',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                Toggle Theme
              </button>
            </div>
          </div>

          {/* SIGN IN / SIGN OUT IN DRAWER */}
          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--border-line)' }}>
            {isAuthenticated() ? (
              <button
                onClick={() => {
                  logoutUser();
                  setIsNavDrawerOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                🚪 Sign Out
              </button>
            ) : (
              <button
                onClick={() => {
                  setIsAuthOpen(true);
                  setIsNavDrawerOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '5px 12px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                  backgroundColor: 'transparent',
                  color: '#f59e0b',
                  border: '2px solid #f59e0b',
                  borderRadius: '30px',
                  fontWeight: '800',
                  cursor: 'pointer',
                }}
              >
                🔐 Sign In
              </button>
            )}
          </div>
        </div>

        {/* 🌌 MAIN SECURITY GATED CONTENT AREA DESK */}
        <main
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: `${headerHeight + 16}px 20px 24px 20px`,
            boxSizing: 'border-box',
          }}
          className="main-content"
        >
          <style>{`
            @media (max-width: 768px) {
              .main-content {
                padding-bottom: 70px !important;
              }
            }
          `}</style>
          <ErrorBoundary>
            <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary, #111827)' }}><div className="animate-pulse">Loading Ebakasi Interface...</div></div> }>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={
                <ProductCatalog
                  products={products}
                  cart={cart}
                  setCart={setCart}
                  category={category}
                  setCategory={setCategory}
                  sortPrice={sortPrice}
                  setSortPrice={setSortPrice}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                />
              } />
              <Route path="/product/:id" element={
                <ProductDetail
                  products={products}
                  cart={cart}
                  setCart={setCart}
                />
              } />
              <Route path="/products/:slug" element={
                <ProductDetail
                  products={products}
                  cart={cart}
                  setCart={setCart}
                />
              } />
              <Route path="/specialists" element={<SpecialistsPortal baseUrl={baseUrl} mode="specialists" />} />
              <Route path="/cart" element={
                <CartSummary
                  cart={cart}
                  setCart={setCart}
                  baseUrl={baseUrl}
                  settings={settings}
                />
              } />
              <Route path="/orders" element={<OrderTracker baseUrl={baseUrl} />} />
              <Route path="/lab" element={<AIResearchLab baseUrl={baseUrl} />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <BuyerDashboard baseUrl={baseUrl} />
                </ProtectedRoute>
              } />
              <Route path="/vendor" element={
                <VendorProtectedRoute>
                  <VendorDashboard baseUrl={baseUrl} />
                </VendorProtectedRoute>
              } />
              <Route path="/machinery-hire" element={
                <MachineryHireProtectedRoute>
                  <MachineryHireDashboard baseUrl={baseUrl} />
                </MachineryHireProtectedRoute>
              } />
              <Route path="/financier" element={
                <FinancierProtectedRoute>
                  <FinancierDashboard baseUrl={baseUrl} />
                </FinancierProtectedRoute>
              } />
              <Route path="/admin" element={
                <AdminProtectedRoute>
                  <AdminDashboard
                    baseUrl={baseUrl}
                    settings={settings}
                    onUpdateSettings={updateGlobalSettingsStateBroadcast}
                  />
                </AdminProtectedRoute>
              } />
              <Route path="/machinery" element={<MachineryCatalog baseUrl={baseUrl} />} />
              <Route path="/logistics" element={
                <LogisticsProtectedRoute>
                  <LogisticsPortal baseUrl={baseUrl} />
                </LogisticsProtectedRoute>
              } />
              <Route path="/about" element={<AboutPage />} />
            </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>

        {/* 🔒 ADMINISTRATIVE 2FA CHALLENGE MODAL */}
        {showMfaModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15,23,42,0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 3000,
            }}
          >
            <div
              style={{
                backgroundColor: 'var(--bg-card)',
                border: '2px solid var(--accent-shout)',
                padding: '30px',
                borderRadius: '12px',
                maxWidth: '400px',
                width: '100%',
                boxSizing: 'border-box',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>🔐</div>
              <h3
                style={{
                  margin: '0 0 10px 0',
                  fontSize: '18px',
                  fontWeight: '900',
                }}
              >
                2-FACTOR IDENTITY GATE
              </h3>
              <p
                style={{
                  fontSize: '12px',
                  color: '#94a3b8',
                  margin: '0 0 20px 0',
                  lineHeight: '1.5',
                }}
              >
                Enter the 6-digit confirmation pin sent via your designated
                authentication profile channel:
              </p>
              <form onSubmit={handleMfaVerificationSubmit}>
                <input
                  type="text"
                  maxLength="6"
                  placeholder="e.g. 749204"
                  required
                  value={mfaCode}
                  onChange={(e) =>
                    setMfaCode(e.target.value.replace(/\D/g, ''))
                  }
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'var(--bg-main)',
                    border: '1px solid var(--border-line)',
                    color: 'var(--text-primary)',
                    borderRadius: '6px',
                    textAlign: 'center',
                    fontSize: '18px',
                    fontWeight: '800',
                    letterSpacing: '4px',
                    outline: 'none',
                    marginBottom: '15px',
                    boxSizing: 'border-box',
                  }}
                />
                <button
                  className="action-btn-pill"
                  type="submit"
                  disabled={isMfaVerifying}
                  style={{
                    width: '100%',
                    padding: '12px 0',
                    backgroundColor: '#ea580c',
                    color: 'var(--text-primary)',
                    border: 'none',
                    textTransform: 'uppercase',
                  }}
                >
                  {isMfaVerifying
                    ? 'Decrypting Security...'
                    : 'Authorize Identity Session'}
                </button>
              </form>
              <span
                onClick={() => {
                  logoutUser();
                  window.location.reload();
                }}
                style={{
                  display: 'block',
                  marginTop: '15px',
                  fontSize: '12px',
                  color: '#64748b',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Cancel & Disconnect
              </span>
            </div>
          </div>
        )}
        
        <LegalCharterModal isOpen={showLegalCharter} onClose={() => setShowLegalCharter(false)} />

        {/* PWA Update Available Banner */}
        {updateReady && (
          <div
            style={{
              position: 'fixed',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--accent-shout)',
              borderRadius: '12px',
              padding: '16px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              zIndex: 9999,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              maxWidth: '90vw',
              width: 'auto',
            }}
          >
            <span
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--text-primary)',
              }}
            >
              A new version of Ebakasi is available.
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleAppUpdate}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--accent-shout)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '700',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                Update
              </button>
              <button
                onClick={() => setUpdateReady(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border-line)',
                  borderRadius: '6px',
                  fontWeight: '600',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        
        {/* COMPACT IN-FLOW FOOTER */}
        <footer
          style={{
            backgroundColor: '#1e293b',
            borderTop: '1px solid #334155',
            padding: '24px 20px',
            marginTop: '20px',
          }}
          className="corporate-footer"
        >
          {/* Uganda Flag Stripe Accent */}
          <div
            style={{
              height: '6px',
              width: '100%',
              background: 'linear-gradient(90deg, #000000 33.33%, #FCDC04 33.33%, #FCDC04 66.66%, #D90000 66.66%)',
              marginBottom: '20px',
            }}
          />
          <style>{`
            @media (max-width: 768px) {
              .corporate-footer {
                padding: '20px 16px';
              }
              .footer-columns {
                grid-template-columns: 1fr !important;
                gap: '24px !important';
              }
              .footer-column {
                min-width: 100% !important;
              }
            }
          `}</style>
          <div
            style={{
              maxWidth: '1400px',
              margin: '0 auto',
            }}
          >
            <div
              className="footer-columns"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '32px',
              }}
            >
              {/* Company/Brand Blurb */}
              <div className="footer-column" style={{ minWidth: 0 }}>
                <h3
                  style={{
                    color: '#cbd5e1',
                    fontSize: '16px',
                    fontWeight: '700',
                    marginBottom: '12px',
                  }}
                >
                  Ebakasi Industrial Uganda
                </h3>
                <p
                  style={{
                    color: '#94a3b8',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    marginBottom: '8px',
                  }}
                >
                  Connecting East African enterprises with global supply chains across all industrial fields: manufacturing, machinery, raw materials, agriculture, processing, and construction.
                </p>
                <p
                  style={{
                    color: '#64748b',
                    fontSize: '12px',
                  }}
                >
                  © {new Date().getFullYear()} Ebakasi. All rights reserved.
                </p>
              </div>

              {/* Portals Link Column */}
              <div className="footer-column" style={{ minWidth: 0 }}>
                <h4
                  style={{
                    color: '#cbd5e1',
                    fontSize: '14px',
                    fontWeight: '700',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                  }}
                >
                  Portals
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <button
                    onClick={() => navigateToView('catalog')}
                    style={{
                      padding: "4px 10px",
                      background: "rgba(30, 41, 59, 0.4)",
                      border: "1px solid var(--border-line)",
                      borderRadius: "16px",
                      color: "var(--text-secondary)",
                      transition: "all 0.2s ease",
                      fontSize: '13px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    📦 Catalog
                  </button>
                  <button
                    onClick={() => navigateToView('orders')}
                    style={{
                      padding: "4px 10px",
                      background: "rgba(30, 41, 59, 0.4)",
                      border: "1px solid var(--border-line)",
                      borderRadius: "16px",
                      color: "var(--text-secondary)",
                      transition: "all 0.2s ease",
                      fontSize: '13px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    📦 Track Orders
                  </button>
                  <button
                    onClick={() => navigateToView('engineers')}
                    style={{
                      padding: "4px 10px",
                      background: "rgba(30, 41, 59, 0.4)",
                      border: "1px solid var(--border-line)",
                      borderRadius: "16px",
                      color: "var(--text-secondary)",
                      transition: "all 0.2s ease",
                      fontSize: '13px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    👷 Engineers
                  </button>
                  <button
                    onClick={() => setIsBoqOpen(true)}
                    style={{
                      padding: "4px 10px",
                      background: "rgba(30, 41, 59, 0.4)",
                      border: "1px solid var(--border-line)",
                      borderRadius: "16px",
                      color: "var(--text-secondary)",
                      transition: "all 0.2s ease",
                      fontSize: '13px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    📋 Upload BOQ
                  </button>
                  <button
                    onClick={() => setShowLegalCharter(true)}
                    style={{
                      padding: "4px 10px",
                      background: "rgba(30, 41, 59, 0.4)",
                      border: "1px solid var(--border-line)",
                      borderRadius: "16px",
                      color: "var(--text-secondary)",
                      transition: "all 0.2s ease",
                      fontSize: '13px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    ⚖️ Legal Charter
                  </button>
                  <button
                    onClick={() => navigateToView('about')}
                    style={{
                      padding: "4px 10px",
                      background: "rgba(30, 41, 59, 0.4)",
                      border: "1px solid var(--border-line)",
                      borderRadius: "16px",
                      color: "var(--text-secondary)",
                      transition: "all 0.2s ease",
                      fontSize: '13px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    🏛️ About Us
                  </button>
                </div>
              </div>

              {/* Contact Column */}
              <div className="footer-column" style={{ minWidth: 0 }}>
                <h4
                  style={{
                    color: '#cbd5e1',
                    fontSize: '14px',
                    fontWeight: '700',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                  }}
                >
                  Contact
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <p
                    style={{
                      color: '#94a3b8',
                      fontSize: '12px',
                      lineHeight: '1.5',
                      margin: 0,
                    }}
                  >
                    The Industrial Exchange, Level 4, Western Wing, Nsambya, Kampala, Uganda
                  </p>
                  <a
                    href="mailto:corporate@ebakasi.com"
                    style={{
                      color: '#94a3b8',
                      fontSize: '13px',
                      textDecoration: 'none',
                    }}
                  >
                    corporate@ebakasi.com
                  </a>
                  <a
                    href="tel:+256790206354"
                    style={{
                      color: '#94a3b8',
                      fontSize: '13px',
                      textDecoration: 'none',
                    }}
                  >
                    +256 (0) 790 206354
                  </a>
                  <a
                    href={`https://wa.me/${String(settings.whatsappSupport).replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#94a3b8',
                      fontSize: '13px',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    💬 WhatsApp: {settings.whatsappSupport}
                  </a>
                </div>
              </div>

              {/* Payment/Trust Badges */}
              <div className="footer-column" style={{ minWidth: 0 }}>
                <h4
                  style={{
                    color: '#cbd5e1',
                    fontSize: '14px',
                    fontWeight: '700',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                  }}
                >
                  Payment Methods
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#94a3b8',
                      fontSize: '12px',
                    }}
                  >
                    📱 MTN MoMo
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#94a3b8',
                      fontSize: '12px',
                    }}
                  >
                    📱 Airtel Money
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#94a3b8',
                      fontSize: '12px',
                    }}
                  >
                    🔒 Ebakasi Secure Escrow
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#94a3b8',
                      fontSize: '12px',
                    }}
                  >
                    🏦 Bank Transfer / EFT
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>

        <BoqModal 
          isOpen={isBoqOpen} 
          onClose={() => setIsBoqOpen(false)} 
          addToCart={(item) => setCart([...cart, { ...item, quantity: item.quantity || 1 }])}
          baseUrl={baseUrl}
        />

        {isAuthOpen && (
          <AuthModal
            isOpen={isAuthOpen}
            onCloseFormDrawer={() => setIsAuthOpen(false)}
            onAuthSuccessBroadcast={(userProfile) => {
              setIsAuthOpen(false);
              // Navigate by role after successful login
              if (userProfile?.role === 'admin') {
                navigateToView('admin');
              } else if (userProfile?.role === 'machinery_hire') {
                navigateToView('machinery-hire');
              } else if (userProfile?.role === 'vendor') {
                navigateToView('vendor');
              } else {
                navigateToView('catalog');
              }
            }}
          />
        )}

        {/* Mobile Bottom Navigation Bar */}
        <div
          style={{
            display: 'none',
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#1e293b',
            borderTop: '1px solid #334155',
            zIndex: 2000,
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          className="mobile-bottom-nav"
        >
          <style>{`
            @media (max-width: 768px) {
              .mobile-bottom-nav {
                display: flex !important;
              }
            }
          `}</style>
          <button
            onClick={() => navigateToView('catalog')}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: "4px 10px",
              background: "rgba(30, 41, 59, 0.4)",
              border: "1px solid var(--border-line)",
              borderRadius: "16px",
              color: "var(--text-secondary)",
              transition: "all 0.2s ease",
              fontSize: '9px',
              fontWeight: '600',
              cursor: 'pointer',
              minHeight: '52px',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <span style={{ fontSize: '18px', marginBottom: '4px' }}>🏠</span>
            Catalog
          </button>
          {checkAuthStatus() && (
            <button
              onClick={() => navigateToView('dashboard')}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: "4px 10px",
                background: "rgba(30, 41, 59, 0.4)",
                border: "1px solid var(--border-line)",
                borderRadius: "16px",
                color: "var(--text-secondary)",
                transition: "all 0.2s ease",
                fontSize: '9px',
                fontWeight: '600',
                cursor: 'pointer',
                minHeight: '52px',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <span style={{ fontSize: '18px', marginBottom: '4px' }}>📊</span>
              Dashboard
            </button>
          )}
          <button
            onClick={() => navigateToView('cart')}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: "4px 10px",
              background: "rgba(30, 41, 59, 0.4)",
              border: "1px solid var(--border-line)",
              borderRadius: "16px",
              color: "var(--text-secondary)",
              transition: "all 0.2s ease",
              fontSize: '9px',
              fontWeight: '600',
              cursor: 'pointer',
              minHeight: '52px',
              WebkitTapHighlightColor: 'transparent',
              position: 'relative',
            }}
          >
            <span style={{ fontSize: '18px', marginBottom: '4px' }}>🛒</span>
            Cart
            {cart.length > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '25%',
                  backgroundColor: '#ea580c',
                  color: '#fff',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  fontSize: '10px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {totalCartPieces}
              </span>
            )}
          </button>
          <button
            onClick={() => setIsNavDrawerOpen(true)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: "4px 10px",
              background: "rgba(30, 41, 59, 0.4)",
              border: "1px solid var(--border-line)",
              borderRadius: "16px",
              color: "var(--text-secondary)",
              transition: "all 0.2s ease",
              fontSize: '9px',
              fontWeight: '600',
              cursor: 'pointer',
              minHeight: '52px',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <span style={{ fontSize: '18px', marginBottom: '4px' }}>☰</span>
            More
          </button>
        </div>

        {/* Floating PWA Install Chip */}
        {shouldShowInstallChip && (
          <div
            style={{
              position: 'fixed',
              bottom: '80px',
              right: '20px',
              zIndex: 1500,
              animation: 'fadeInUp 0.5s ease-out',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: 'var(--accent-shout)',
                color: '#0f172a',
                borderRadius: '30px',
                fontSize: '13px',
                fontWeight: '700',
                boxShadow: '0 4px 12px rgba(234, 88, 12, 0.4)',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                position: 'relative',
              }}
              onClick={isIOS ? handleIosInstallClick : handleInstallClick}
            >
              <span>📲</span>
              <span>Install App</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSnoozeInstall();
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0f172a',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  padding: '0 4px',
                  lineHeight: 1,
                  marginLeft: '4px',
                }}
              >
                ×
              </button>
            </div>

            {/* iOS Install Instruction Tooltip */}
            {isIOS && showIosTooltip && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  right: '0',
                  marginBottom: '8px',
                  padding: '12px 16px',
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  whiteSpace: 'nowrap',
                  border: '1px solid var(--border-line)',
                  animation: 'fadeIn 0.3s ease-out',
                }}
              >
                Tap the Share icon, then "Add to Home Screen"
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-6px',
                    right: '20px',
                    width: '12px',
                    height: '12px',
                    backgroundColor: 'var(--bg-card)',
                    borderRight: '1px solid var(--border-line)',
                    borderBottom: '1px solid var(--border-line)',
                    transform: 'rotate(45deg)',
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Add keyframe animations to document */}
        <style>
          {`
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
          `}
        </style>
      </div>
    </div>
  );
}

// Main App component that wraps AppContent with HelmetProvider
export default function App() {
  return (
    <HelmetProvider>
      <AppContent />
    </HelmetProvider>
  );
}
